import React, { useState, useEffect } from 'react';
import { analytics } from '../services/analytics';
import { useTranslation } from 'react-i18next';
import { X, Shield, BarChart2 } from 'lucide-react';

/**
 * Unified Privacy Consent Component
 *
 * Lovable-style UI: small floating box (bottom-right) → side panel on "Manage preferences".
 * All logic (localStorage keys, analytics calls) is identical to the previous banner version.
 *
 * localStorage keys used:
 *   cookiesAccepted / cookiesRejected
 *   analytics-consent  ('accepted' | 'declined')
 */
const UnifiedPrivacyConsent: React.FC = () => {
  const { t } = useTranslation('consent');
  const [showConsent, setShowConsent] = useState(false);
  const [showPanel, setShowPanel] = useState(false);
  const [consentGiven, setConsentGiven] = useState<boolean | null>(null);
  const getStoredAnalyticsEnabled = () => {
    try {
      const pref = localStorage.getItem('analytics-consent');
      if (pref === 'accepted') return true;
      if (pref === 'declined') return false;
      return true;
    } catch {
      return true;
    }
  };

  const [analyticsEnabled, setAnalyticsEnabled] = useState(getStoredAnalyticsEnabled);

  useEffect(() => {
    const cookieConsent = localStorage.getItem('cookiesAccepted');
    const cookieRejected = localStorage.getItem('cookiesRejected');
    const analyticsConsent = localStorage.getItem('analytics-consent');

    if ((!cookieConsent && !cookieRejected) || !analyticsConsent) {
      setShowConsent(true);
      // Pre-populate toggle from any previously saved analytics preference
      if (analyticsConsent) {
        setAnalyticsEnabled(analyticsConsent === 'accepted');
      }
    } else {
      const hasConsent = cookieConsent === 'true' && analyticsConsent === 'accepted';
      setConsentGiven(hasConsent);
      setAnalyticsEnabled(analyticsConsent === 'accepted');

      if (analyticsConsent === 'accepted') {
        analytics.enableAnalytics();
      } else {
        analytics.disableAnalytics();
      }
    }
  }, []);

  // ── Accept all ──────────────────────────────────────────────────────────────
  const handleAcceptAll = () => {
    localStorage.setItem('cookiesAccepted', 'true');
    localStorage.setItem('analytics-consent', 'accepted');
    localStorage.removeItem('cookiesRejected');
    setAnalyticsEnabled(true);
    setConsentGiven(true);
    setShowConsent(false);
    setShowPanel(false);
    analytics.enableAnalytics();
    analytics.trackEvent({
      action: 'privacy_consent_given',
      category: 'Privacy',
      label: 'unified_consent_accept_all',
      custom_parameters: {
        consent_types: ['cookies', 'analytics'],
        consent_method: 'banner_accept_all',
        timestamp: new Date().toISOString(),
      },
    });
  };

  // ── Save granular preferences (from side panel) ─────────────────────────────
  const handleSavePreferences = () => {
    localStorage.setItem('cookiesAccepted', 'true');
    localStorage.removeItem('cookiesRejected');
    localStorage.setItem('analytics-consent', analyticsEnabled ? 'accepted' : 'declined');
    setConsentGiven(analyticsEnabled);
    setShowConsent(false);
    setShowPanel(false);
    if (analyticsEnabled) {
      analytics.enableAnalytics();
    } else {
      analytics.disableAnalytics();
    }
  };

  // ── Reject all ──────────────────────────────────────────────────────────────
  const handleRejectAll = () => {
    localStorage.setItem('cookiesRejected', 'true');
    localStorage.setItem('analytics-consent', 'declined');
    localStorage.removeItem('cookiesAccepted');
    setAnalyticsEnabled(false);
    setConsentGiven(false);
    setShowConsent(false);
    setShowPanel(false);
    analytics.disableAnalytics();
  };

  // ── Re-open consent UI (from the small lock button) ────────────────────────
  // Preserves the user's last saved preference so the panel reflects reality.
  const handleRevoke = () => {
    setAnalyticsEnabled(getStoredAnalyticsEnabled());
    setConsentGiven(null);
    setShowConsent(true);
    setShowPanel(false);
  };

  const handleOpenPanel = () => {
    setAnalyticsEnabled(getStoredAnalyticsEnabled());
    setShowPanel(true);
  };

  // ── After consent is given: small persistent lock button ───────────────────
  if (!showConsent && consentGiven !== null) {
    return (
      <button
        onClick={handleRevoke}
        className="fixed bottom-4 left-4 bg-gray-600 text-white text-xs px-3 py-1 rounded-full hover:bg-gray-700 transition z-40"
        title={t('consent.privacy_settings_title')}
      >
        🔒 {t('consent.privacy_settings_button')}
      </button>
    );
  }

  if (!showConsent) return null;

  return (
    <>
      {/* ── Small floating box (bottom-right) ──────────────────────────────── */}
      {!showPanel && (
        <div className="fixed bottom-4 right-4 z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-2xl shadow-xl p-5 w-[min(300px,calc(100vw-2rem))]">
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">
            {t('consent.title')}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-4 leading-relaxed">
            {t('consent.description')}{' '}
            <a
              href="/privacy-policy"
              className="text-blue-600 dark:text-blue-400 hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('consent.learn_more')}
            </a>
          </p>
          <div className="flex flex-col gap-2">
            <button
              onClick={handleAcceptAll}
              className="w-full bg-blue-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition"
            >
              {t('consent.accept_all')}
            </button>
            <button
              onClick={handleOpenPanel}
              className="w-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-500 text-gray-700 dark:text-gray-300 py-2 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-600 transition"
            >
              Manage preferences
            </button>
          </div>
        </div>
      )}

      {/* ── Side panel ─────────────────────────────────────────────────────── */}
      {showPanel && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/20 z-50"
            onClick={() => setShowPanel(false)}
          />

          {/* Drawer – starts below the navbar */}
          {/* Previous: top-16 h-[calc(100vh-4rem)] (before slim header) */}
          <div
            className="fixed right-0 w-full max-w-sm bg-white dark:bg-gray-800 z-50 shadow-2xl overflow-y-auto rounded-tl-xl"
            style={{ top: 'var(--app-header-h, 64px)', height: 'calc(100vh - var(--app-header-h, 64px))' }}
          >
            <div className="p-6">

              {/* Header */}
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                  {t('consent.privacy_settings')}
                </h2>
                <button
                  onClick={() => setShowPanel(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed mb-6">
                When you visit websites, they may store or retrieve information on your browser
                through cookies and similar technologies. You have the option of disabling
                certain types of cookies, though doing so may impact your experience.
              </p>

              {/* Essential — always on */}
              <div className="mb-6 pb-6 border-b border-gray-100 dark:border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {t('consent.essential_cookies')}
                    </span>
                  </div>
                  {/* Always-on toggle (not clickable) */}
                  <div
                    className="w-10 h-6 bg-blue-600 rounded-full flex items-center px-1 cursor-not-allowed"
                    title="Cannot be disabled"
                  >
                    <div className="w-4 h-4 bg-white rounded-full ml-auto" />
                  </div>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed mb-1">
                  {t('consent.essential_cookies_desc')}
                </p>
                <a
                  href="/privacy-policy"
                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Disclosures
                </a>
              </div>

              {/* Analytics — toggleable */}
              <div className="mb-8 pb-6 border-b border-gray-100 dark:border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <BarChart2 className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {t('consent.analytics')}
                    </span>
                  </div>
                  <button
                    onClick={() => setAnalyticsEnabled((prev) => !prev)}
                    className={`w-10 h-6 rounded-full flex items-center px-1 transition-colors ${
                      analyticsEnabled ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'
                    }`}
                    aria-pressed={analyticsEnabled}
                    aria-label="Toggle analytics"
                  >
                    <div
                      className={`w-4 h-4 bg-white rounded-full shadow transition-transform ${
                        analyticsEnabled ? 'translate-x-4' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed mb-1">
                  {t('consent.analytics_desc')}
                </p>
                <a
                  href="/privacy-policy"
                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Disclosures
                </a>
              </div>

              {/* Actions */}
              <div className="flex flex-col gap-2">
                <button
                  onClick={handleSavePreferences}
                  className="w-full bg-blue-600 text-white py-3 rounded-lg text-sm font-semibold hover:bg-blue-700 transition"
                >
                  Save
                </button>
                <button
                  onClick={handleRejectAll}
                  className="w-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-500 text-gray-600 dark:text-gray-300 py-2.5 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-600 transition"
                >
                  {t('consent.reject_all')}
                </button>
              </div>

            </div>
          </div>
        </>
      )}
    </>
  );
};

export default UnifiedPrivacyConsent;
